<script setup lang="ts">
import { IconClock, IconPencil } from '@tabler/icons-vue';
import {
    PButton,
    PCollapsible,
    PCollapsibleContent,
    PCollapsibleTrigger,
    PSeparator,
    PToggleGroup,
    PToggleGroupItem,
} from '@pokerly/ui';
import { computed, ref, watch } from 'vue';

interface Props {
    suggestion: string | number | null;
    estimationOptions: readonly (string | number)[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
    confirm: [value: string | number];
}>();

const isEditPanelOpen = ref(false);
const isFullyCollapsed = ref(true);
const selectedEstimate = ref<string | number | null>(null);

const estimationOptionEntries = computed(() =>
    props.estimationOptions.map((value, index) => ({
        id: `${index}:${String(value)}`,
        label: String(value),
        value,
    })),
);

const selectedEstimateKey = computed<string | undefined>({
    get() {
        if (selectedEstimate.value === null) {
            return undefined;
        }

        return estimationOptionEntries.value.find((option) => option.value === selectedEstimate.value)?.id;
    },
    set(id) {
        const selectedOption = estimationOptionEntries.value.find((option) => option.id === id);
        selectedEstimate.value = selectedOption?.value ?? null;
    },
});

watch(isEditPanelOpen, (open) => {
    if (open) {
        selectedEstimate.value = props.suggestion;
    }
});

function onOpenChange(open: boolean) {
    isEditPanelOpen.value = open;
    if (open) {
        isFullyCollapsed.value = false;
    }
}

function onAnimationEnd(event: AnimationEvent) {
    if (event.animationName === 'slideUp') {
        isFullyCollapsed.value = true;
    }
}

function cancel() {
    isEditPanelOpen.value = false;
}

function confirm() {
    if (selectedEstimate.value !== null) {
        emit('confirm', selectedEstimate.value);
    }
    isEditPanelOpen.value = false;
}
</script>

<template>
    <PCollapsible :open="isEditPanelOpen" @update:open="onOpenChange">
        <div class="flex gap-2 p-2 items-center">
            <IconClock class="size-5" />
            <div class="grow flex flex-col">
                <span class="paragraph-sm font-bold text-foreground-primary">Recommended estimate</span>
                <span class="paragraph-xs text-foreground-muted">Suggested from the vote median</span>
            </div>
            <div class="flex gap-2 p-2 items-center">
                <h1 v-if="suggestion !== null">{{ suggestion }}</h1>
                <span v-else class="text-foreground-muted">—</span>
                <PCollapsibleTrigger as-child v-if="false">
                    <PButton variant="secondary" rounded size="icon">
                        <IconPencil />
                    </PButton>
                </PCollapsibleTrigger>
            </div>
        </div>
        <div class="mt-2">
            <PSeparator v-if="isFullyCollapsed" />
            <PCollapsibleContent
                v-if="false"
                class="rounded-lg border border-border-default"
                @animationend="onAnimationEnd"
            >
                <div class="p-4 space-y-6">
                    <div class="flex items-start justify-between gap-4">
                        <div class="flex flex-col">
                            <span class="paragraph-sm font-bold text-foreground-primary">Adjust final estimate</span>
                            <span class="paragraph-xs text-foreground-muted"
                                >Select a different value from your deck.</span
                            >
                        </div>
                        <div class="flex gap-2 shrink-0">
                            <PButton variant="outline" size="sm" @click="cancel">Cancel</PButton>
                            <PButton size="sm" @click="confirm">Confirm</PButton>
                        </div>
                    </div>
                    <PToggleGroup
                        v-model="selectedEstimateKey"
                        type="single"
                        variant="outline"
                        size="lg"
                        :spacing="4"
                        class="flex-wrap justify-center py-4 w-full"
                        aria-label="Final estimate options"
                    >
                        <PToggleGroupItem
                            v-for="option in estimationOptionEntries"
                            :key="option.id"
                            :value="option.id"
                            class="min-w-15"
                        >
                            {{ option.label }}
                        </PToggleGroupItem>
                    </PToggleGroup>
                </div>
            </PCollapsibleContent>
        </div>
    </PCollapsible>
</template>
